import React from 'react';
import { Grid, Tooltip } from '@mui/material';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import { ButtonLink, Icon, ImageAvatar, SquareChip, TagsArray, Text, useSettings } from 'app/main/components';
import { CurrencySquareChip, DateRangeSquareChip } from 'app/main/components/list-items';
import buttons from './ContractsButtons';

export default function ContractsListItem(props: ItemLayoutProps) {
	const { t } = useSettings();

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="companies"
			uid={props.model.cntr_comp_uid}
			avatar={props.model.comp_avatar}
			initials={props.model.cntr_title_initials}
			labelBadge={props.model.cnst_title_initials}
			labelBadgeTooltip={props.model.cnst_title}
			badgeColor={props.model.cnst_color}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={props.width >= 600 ? 6 : 12}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.cntr_warning_desc && (
								<Tooltip title={props.model.cntr_warning_desc} arrow>
									<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
								</Tooltip>
							)}
							{/* BLOCKED */}
							{props.model.cntr_blocked_desc && (
								<Tooltip title={props.model.cntr_blocked_desc} arrow>
									<Icon className="mr-2" icon="block" color="error.main" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={{ module: 'contracts', uid: props.model.cntr_uid }}
							>
								{props.model.cntr_title}
								{props.model.comp_title && ` - ${props.model.comp_title}`}
							</Text>
						</Grid>
						{/* TYPE */}
						<Grid>
							{props.model.cnty_title && (
								<SquareChip label={props.model.cnty_title} backgroundColor={props.model.cnty_color} />
							)}
						</Grid>
						{/* DATE FROM - TO */}
						<Grid>
							<DateRangeSquareChip from={props.model.cntr_d_from} to={props.model.cntr_d_to} />
						</Grid>
					</Grid>
				</Grid>
				{props.width >= 600 && (
					<Grid size={props.width >= 960 ? 3 : 6}>
						<Grid container>
							{/* INTERNAL ASSIGNED USER */}
							<Grid size={12}>
								<ButtonLink
									href={`/users/details/${props.model.cntr_user_assigned_uid}`}
									icon="account-circle"
									fallback={t('VIEW_ITEM_NO_INTUSER')}
								>
									{props.model.user_assigned_title}
								</ButtonLink>
							</Grid>
							{/* EXTERNAL REFERENCE */}
							<Grid size={12}>
								<ButtonLink
									href={`/people/details/${props.model.cntr_peop_uid}`}
									icon="card-account-details-outline"
									fallback={t('VIEW_ITEM_NO_EXTREF')}
								>
									{props.model.peop_title}
								</ButtonLink>
							</Grid>
						</Grid>
					</Grid>
				)}
				{props.width >= 960 && (
					<Grid size={3}>
						<Grid container>
							{/* TOTAL VALUE */}
							<Grid>
								<CurrencySquareChip
									id="contractsListItem.totValue"
									defaultMessage={t('VALUE')}
									value={props.model.cntr_tot_value}
								/>
							</Grid>
							{/* TAGS */}
							<Grid size={12}>
								<TagsArray tags={props.model.cntr_tags} changeFilter={props.changeFilter} />
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		</GridListItem>
	);
}
